class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.activeElement = null;
    this.handleCartTriggerClick = this.handleCartTriggerClick.bind(this);
    this.handleCartTriggerKeydown = this.handleCartTriggerKeydown.bind(this);

    this.addEventListener('keyup', (event) => {
      if (event.code === 'Escape') this.close();
    });

    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) overlay.addEventListener('click', this.close.bind(this));

    /*
      Delegación global: sigue funcionando cuando Shopify vuelve a renderizar
      el icono #cart-icon-bubble después de añadir o actualizar productos.
    */
    document.addEventListener('click', this.handleCartTriggerClick, true);
    document.addEventListener('keydown', this.handleCartTriggerKeydown, true);

    this.setHeaderCartIconAccessibility();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.handleCartTriggerClick, true);
    document.removeEventListener('keydown', this.handleCartTriggerKeydown, true);
  }

  getCartTrigger(event) {
    if (!(event.target instanceof Element)) return null;

    return event.target.closest(
      '#cart-icon-bubble, .header__icon--cart, [data-cart-icon-bubble]'
    );
  }

  handleCartTriggerClick(event) {
    const cartLink = this.getCartTrigger(event);
    if (!cartLink || cartLink.closest('cart-drawer')) return;

    event.preventDefault();
    event.stopPropagation();
    this.open(cartLink);
  }

  handleCartTriggerKeydown(event) {
    if (event.code.toUpperCase() !== 'SPACE') return;

    const cartLink = this.getCartTrigger(event);
    if (!cartLink || cartLink.closest('cart-drawer')) return;

    event.preventDefault();
    this.open(cartLink);
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) {
      this.setSummaryAccessibility(cartDrawerNote);
    }

    window.setTimeout(() => {
      this.classList.add('animate', 'active');
      document.body.classList.add('overflow-hidden', 'einef-cart-drawer-open');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');

        const focusElement =
          this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');

        if (
          containerToTrapFocusOn &&
          focusElement &&
          typeof trapFocus === 'function'
        ) {
          trapFocus(containerToTrapFocusOn, focusElement);
        }
      },
      { once: true }
    );
  }

  close() {
    this.classList.remove('active');

    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.activeElement);
    }

    document.body.classList.remove('overflow-hidden', 'einef-cart-drawer-open');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling?.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute(
        'aria-expanded',
        !event.currentTarget.closest('details').hasAttribute('open')
      );
    });

    if (typeof onKeyUpEscape === 'function') {
      cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
    }
  }

  renderContents(parsedState) {
    this.classList.remove('is-empty');
    this.querySelector('cart-drawer-items')?.classList.remove('is-empty');
    this.productId = parsedState.id;

    const drawerMarkup = parsedState.sections?.['cart-drawer'];
    if (drawerMarkup) {
      const drawerTarget = document.getElementById('CartDrawer');
      const drawerSource = this.getSectionDOM(drawerMarkup, '#CartDrawer');

      if (drawerTarget && drawerSource) {
        drawerTarget.innerHTML = drawerSource.innerHTML;
      }
    }

    const iconMarkup = parsedState.sections?.['cart-icon-bubble'];
    if (iconMarkup) {
      const iconSource = this.getSectionDOM(iconMarkup, '#cart-icon-bubble');
      const iconTarget = document.getElementById('cart-icon-bubble');

      if (iconSource && iconTarget) {
        iconTarget.replaceWith(iconSource);
        this.setHeaderCartIconAccessibility();
      }
    }

    window.setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) overlay.addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const element = this.getSectionDOM(html, selector);
    return element ? element.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

if (typeof CartItems !== 'undefined' && !customElements.get('cart-drawer-items')) {
  class CartDrawerItems extends CartItems {
    getSectionsToRender() {
      return [
        {
          id: 'CartDrawer',
          section: 'cart-drawer',
          selector: '.drawer__inner',
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section',
        },
      ];
    }
  }

  customElements.define('cart-drawer-items', CartDrawerItems);
}
